import React, { useState, useEffect } from 'react';
import { panSpiritApi, resolveApiUrl } from '../api';
import { useAuth } from '../AuthContext';

interface PanSpiritDiary {
  id: number;
  content: string;
  mood: string;
  diary_type: string;
  date: string;
  created_at: string;
  author_name: string;
  author_avatar: string;
  author_id: number | null;
  like_count: number;
  comment_count: number;
  is_liked: boolean;
  images: { id: number; url: string; created_at?: string }[];
}

interface Comment {
  id: number;
  diary_id: number;
  user_id: number | null;
  user_name: string;
  user_avatar: string;
  content: string;
  created_at: string;
}

interface PanSpiritWebProps {
  onClose: () => void;
  onPublish: () => void;
  onOpenPeople: () => void;
}

const TYPE_ICONS: Record<string, string> = {
  note: '📋', mood: '💭', fitness: '💪', tech: '💻', grudge: '😤', gratitude: '🙏',
};

const PanSpiritWeb: React.FC<PanSpiritWebProps> = ({ onClose, onPublish, onOpenPeople }) => {
  const { user: authUser } = useAuth();
  const [diaries, setDiaries] = useState<PanSpiritDiary[]>([]);
  const [loading, setLoading] = useState(true);
  const [anchorPoints, setAnchorPoints] = useState(0);
  const [expandedComments, setExpandedComments] = useState<number | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
  const [feedMode, setFeedMode] = useState<'all' | 'mine'>('all');

  useEffect(() => {
    loadFeed();
    loadAnchor();
  }, []);

  const loadFeed = async () => {
    try {
      setLoading(true);
      const res = await panSpiritApi.getFeed();
      setDiaries(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('加载泛灵之网失败:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadAnchor = async () => {
    try {
      const res = await panSpiritApi.getMyAnchor();
      setAnchorPoints(res.data.anchor_points || 0);
    } catch (err) {
      console.error('加载锚点失败:', err);
    }
  };

  const handleLike = async (diaryId: number) => {
    try {
      const res = await panSpiritApi.toggleLike(diaryId);
      setDiaries(prev =>
        prev.map(d =>
          d.id === diaryId
            ? { ...d, is_liked: res.data.liked, like_count: res.data.like_count }
            : d
        )
      );
      loadAnchor();
    } catch (err) {
      console.error('点赞失败:', err);
    }
  };

  const toggleComments = async (diaryId: number) => {
    if (expandedComments === diaryId) {
      setExpandedComments(null);
      setComments([]);
      return;
    }
    setExpandedComments(diaryId);
    setCommentsLoading(true);
    try {
      const res = await panSpiritApi.getComments(diaryId);
      setComments(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('加载评论失败:', err);
    } finally {
      setCommentsLoading(false);
    }
  };

  const handleAddComment = async (diaryId: number) => {
    if (!commentText.trim()) return;
    setSubmittingComment(true);
    try {
      const res = await panSpiritApi.addComment(diaryId, commentText.trim());
      setComments(prev => [...prev, res.data]);
      setCommentText('');
      setDiaries(prev =>
        prev.map(d =>
          d.id === diaryId ? { ...d, comment_count: d.comment_count + 1 } : d
        )
      );
    } catch (err) {
      console.error('评论失败:', err);
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleDeleteComment = async (diaryId: number, commentId: number) => {
    if (!window.confirm('确定删除这条评论？')) return;
    try {
      await panSpiritApi.deleteComment(diaryId, commentId);
      setComments(prev => prev.filter(c => c.id !== commentId));
      setDiaries(prev =>
        prev.map(d =>
          d.id === diaryId ? { ...d, comment_count: Math.max(0, d.comment_count - 1) } : d
        )
      );
    } catch (err) {
      console.error('删除评论失败:', err);
    }
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - d.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return '刚刚';
    if (mins < 60) return `${mins}分钟前`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}小时前`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}天前`;
    return d.toLocaleDateString('zh-CN', { month: 'short', day: 'numeric' });
  };

  const visibleDiaries = feedMode === 'mine'
    ? diaries.filter(diary => diary.author_id === authUser?.id)
    : diaries;

  return (
    <div className="ps-root">
      {/* Header */}
      <header className="ps-header">
        <button className="ps-back" onClick={onClose}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
        </button>
        <div className="ps-header-center">
          <h1 className="ps-title">🕸️ 泛灵之网</h1>
          <p className="ps-subtitle">The Pan-Spirit Web</p>
        </div>
        <div className="ps-anchor-badge">
          <span className="ps-anchor-icon">⚓</span>
          <span className="ps-anchor-value">{anchorPoints}</span>
        </div>
      </header>

      <section className="ps-social-bar">
        <div className="ps-feed-tabs" aria-label="动态筛选">
          <button className={feedMode === 'all' ? 'active' : ''} onClick={() => setFeedMode('all')}>全网回响</button>
          <button className={feedMode === 'mine' ? 'active' : ''} onClick={() => setFeedMode('mine')}>我的发布</button>
        </div>
        <div className="ps-social-actions">
          <button className="ps-people-btn" onClick={onOpenPeople}>关系簿</button>
          <button className="ps-refresh-btn" onClick={loadFeed} aria-label="刷新动态">↻</button>
          <button className="ps-publish-btn" onClick={onPublish}>发布回响</button>
        </div>
      </section>

      {/* Content */}
      <main className="ps-content">
        {loading ? (
          <div className="ps-loading">
            <div className="ps-spinner" />
            <p>连接泛灵之网...</p>
          </div>
        ) : visibleDiaries.length === 0 ? (
          <div className="ps-empty">
            <div className="ps-empty-icon">🕸️</div>
            <h3>{feedMode === 'mine' ? '你还没有公开回响' : '泛灵之网尚无回响'}</h3>
            <p>分享此刻的见闻与心绪，让连接在网中发生</p>
            <button className="ps-empty-publish" onClick={onPublish}>发布第一条回响</button>
          </div>
        ) : (
          <div className="ps-feed">
            {visibleDiaries.map((diary, idx) => (
              <div key={diary.id} className="ps-card" style={{ animationDelay: `${idx * 50}ms` }}>
                {/* Card Header */}
                <div className="ps-card-header">
                  <div className="ps-author">
                    <div className="ps-avatar">
                      {diary.author_avatar
                        ? <img src={resolveApiUrl(diary.author_avatar)} alt="" className="ps-avatar-img" />
                        : <span>{diary.author_name?.charAt(0) || '?'}</span>
                      }
                    </div>
                    <div className="ps-author-info">
                      <span className="ps-author-name">{diary.author_name || '匿名旅者'}</span>
                      <span className="ps-card-time">{formatDate(diary.date || diary.created_at)}</span>
                    </div>
                  </div>
                  <span className="ps-type-badge">
                    {TYPE_ICONS[diary.diary_type] || '📋'} {diary.mood && <span>{diary.mood}</span>}
                  </span>
                </div>

                {/* Card Body */}
                <div className="ps-card-body">{diary.content}</div>

                {/* Images */}
                {diary.images && diary.images.length > 0 && (
                  <div className="ps-card-images">
                    {diary.images.slice(0, 9).map(img => (
                      <button key={img.id} className="ps-img-btn" onClick={() => setPreviewImageUrl(resolveApiUrl(img.url))}>
                        <img src={resolveApiUrl(img.url)} alt="" className="ps-img-thumb" />
                      </button>
                    ))}
                  </div>
                )}

                {/* Actions */}
                <div className="ps-card-actions">
                  <button
                    className={`ps-action-btn ${diary.is_liked ? 'liked' : ''}`}
                    onClick={() => handleLike(diary.id)}
                  >
                    <span className="ps-action-icon">{diary.is_liked ? '❤️' : '🤍'}</span>
                    <span>{diary.like_count || 0}</span>
                  </button>
                  <button
                    className={`ps-action-btn ${expandedComments === diary.id ? 'active' : ''}`}
                    onClick={() => toggleComments(diary.id)}
                  >
                    <span className="ps-action-icon">💬</span>
                    <span>{diary.comment_count || 0}</span>
                  </button>
                </div>

                {/* Comments Section */}
                {expandedComments === diary.id && (
                  <div className="ps-comments">
                    {commentsLoading ? (
                      <div className="ps-comments-loading">加载评论中...</div>
                    ) : (
                      <>
                        {comments.length === 0 && (
                          <div className="ps-no-comments">暂无评论，来说点什么吧</div>
                        )}
                        {comments.map(c => (
                          <div key={c.id} className="ps-comment">
                            <div className="ps-comment-avatar">
                              {c.user_name?.charAt(0) || '?'}
                            </div>
                            <div className="ps-comment-body">
                              <div className="ps-comment-header">
                                <span className="ps-comment-name">{c.user_name}</span>
                                <span className="ps-comment-time">{formatDate(c.created_at)}</span>
                                {c.user_id === authUser?.id && (
                                  <button className="ps-comment-del" onClick={() => handleDeleteComment(diary.id, c.id)}>删除</button>
                                )}
                              </div>
                              <div className="ps-comment-text">{c.content}</div>
                            </div>
                          </div>
                        ))}
                        <div className="ps-comment-input">
                          <input
                            type="text"
                            value={commentText}
                            onChange={e => setCommentText(e.target.value)}
                            placeholder="写下你的回响..."
                            className="ps-comment-field"
                            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddComment(diary.id); } }}
                          />
                          <button
                            className="ps-comment-send"
                            onClick={() => handleAddComment(diary.id)}
                            disabled={submittingComment || !commentText.trim()}
                          >
                            {submittingComment ? '...' : '发送'}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Image Preview */}
      {previewImageUrl && (
        <div className="ps-preview-overlay" onClick={() => setPreviewImageUrl(null)}>
          <img src={previewImageUrl} alt="预览" className="ps-preview-img" onClick={e => e.stopPropagation()} />
        </div>
      )}

      <style>{`
        .ps-root {
          min-height: 100vh;
          background: #030712;
          background-image:
            radial-gradient(circle at 20% 20%, rgba(88, 28, 135, 0.15) 0%, transparent 50%),
            radial-gradient(circle at 80% 80%, rgba(30, 58, 138, 0.12) 0%, transparent 50%),
            radial-gradient(circle at 50% 50%, rgba(15, 23, 42, 1) 0%, #030712 100%);
          color: #e2e8f0;
          padding-bottom: 40px;
        }

        /* Header */
        .ps-header {
          display: flex; align-items: center; justify-content: space-between;
          padding: 20px 20px 16px;
          padding-top: calc(20px + var(--status-bar-height, 28px));
          background: rgba(15, 23, 42, 0.8);
          backdrop-filter: blur(12px);
          border-bottom: 1px solid rgba(255,255,255,0.06);
          position: sticky; top: 0; z-index: 100;
        }
        .ps-back {
          width: 36px; height: 36px; border-radius: 50%;
          border: 1px solid rgba(148,163,184,0.2);
          background: rgba(30,41,59,0.6);
          color: #94a3b8; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          transition: all 150ms;
        }
        .ps-back:active { transform: scale(0.9); background: rgba(30,41,59,0.9); }
        .ps-header-center { text-align: center; flex: 1; }
        .ps-title { margin: 0; font-size: 18px; font-weight: 800; color: #e2e8f0; }
        .ps-subtitle { margin: 2px 0 0; font-size: 11px; color: rgba(148,163,184,0.5); letter-spacing: 1px; }
        .ps-anchor-badge {
          display: flex; align-items: center; gap: 4px;
          padding: 6px 12px; border-radius: 20px;
          background: linear-gradient(135deg, rgba(139,92,246,0.15), rgba(59,130,246,0.15));
          border: 1px solid rgba(139,92,246,0.25);
        }
        .ps-anchor-icon { font-size: 14px; }
        .ps-anchor-value { font-size: 14px; font-weight: 700; color: #a78bfa; }

        .ps-social-bar {
          max-width:600px; margin:0 auto; padding:12px 20px 0;
          display:flex; align-items:center; justify-content:space-between; gap:10px;
        }
        .ps-feed-tabs { display:flex; gap:4px; padding:4px; border-radius:14px; background:rgba(15,23,42,.78); border:1px solid rgba(148,163,184,.1); }
        .ps-feed-tabs button { min-height:34px; padding:0 12px; border:0; border-radius:10px; background:transparent; color:#64748b; font:700 12px inherit; cursor:pointer; }
        .ps-feed-tabs button.active { color:#f5f3ff; background:linear-gradient(135deg,rgba(124,58,237,.75),rgba(13,148,136,.6)); }
        .ps-social-actions { display:flex; align-items:center; gap:6px; }
        .ps-people-btn, .ps-refresh-btn, .ps-publish-btn, .ps-empty-publish {
          min-height:36px; border-radius:11px; border:1px solid rgba(148,163,184,.13);
          background:rgba(30,41,59,.62); color:#cbd5e1; padding:0 11px; font:750 12px inherit; cursor:pointer;
        }
        .ps-refresh-btn { width:36px; padding:0; font-size:18px; }
        .ps-publish-btn, .ps-empty-publish { border:0; color:white; background:linear-gradient(135deg,#7c3aed,#0f766e); }
        .ps-empty-publish { margin-top:18px; padding:0 18px; }

        /* Content */
        .ps-content { max-width: 600px; margin: 0 auto; padding: 16px 20px; }
        .ps-loading {
          display: flex; flex-direction: column; align-items: center;
          padding: 80px 20px; gap: 16px;
        }
        .ps-spinner {
          width: 36px; height: 36px;
          border: 3px solid rgba(51,65,85,0.5);
          border-top-color: #8b5cf6;
          border-radius: 50%;
          animation: psSpin 0.8s linear infinite;
        }
        @keyframes psSpin { to { transform: rotate(360deg); } }
        .ps-loading p { color: rgba(148,163,184,0.6); font-size: 14px; }

        .ps-empty {
          text-align: center; padding: 80px 20px; color: rgba(148,163,184,0.6);
        }
        .ps-empty-icon { font-size: 64px; margin-bottom: 16px; }
        .ps-empty h3 { margin: 0 0 8px; color: #e2e8f0; font-size: 18px; }
        .ps-empty p { margin: 0; font-size: 14px; line-height: 1.6; }

        /* Feed */
        .ps-feed { display: flex; flex-direction: column; gap: 16px; }
        .ps-card {
          background: rgba(17, 24, 39, 0.7);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 16px;
          overflow: hidden;
          animation: psCardIn 400ms cubic-bezier(0.16,1,0.3,1) both;
        }
        @keyframes psCardIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }

        .ps-card-header {
          display: flex; justify-content: space-between; align-items: center;
          padding: 14px 16px 0;
        }
        .ps-author { display: flex; align-items: center; gap: 10px; }
        .ps-avatar {
          width: 36px; height: 36px; border-radius: 50%;
          background: linear-gradient(135deg, #7c3aed, #3b82f6);
          display: flex; align-items: center; justify-content: center;
          color: white; font-size: 14px; font-weight: 700;
          overflow: hidden; flex-shrink: 0;
        }
        .ps-avatar-img { width: 100%; height: 100%; object-fit: cover; }
        .ps-author-info { display: flex; flex-direction: column; }
        .ps-author-name { font-size: 14px; font-weight: 600; color: #e2e8f0; }
        .ps-card-time { font-size: 11px; color: rgba(148,163,184,0.5); margin-top: 1px; }
        .ps-type-badge {
          font-size: 13px; padding: 4px 8px; border-radius: 8px;
          background: rgba(30,41,59,0.6); color: rgba(148,163,184,0.8);
        }

        .ps-card-body {
          padding: 12px 16px;
          font-size: 15px; line-height: 1.7;
          white-space: pre-wrap; word-break: break-word;
          color: rgba(226,232,240,0.95);
        }

        .ps-card-images {
          padding: 0 16px 8px;
          display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px;
        }
        .ps-img-btn {
          padding: 0; border: none; background: none; cursor: pointer;
          border-radius: 10px; overflow: hidden;
        }
        .ps-img-thumb {
          width: 100%; height: 84px; object-fit: cover; display: block;
          border-radius: 10px; border: 1px solid rgba(255,255,255,0.06);
        }

        .ps-card-actions {
          display: flex; gap: 4px; padding: 8px 16px 12px;
          border-top: 1px solid rgba(255,255,255,0.04);
        }
        .ps-action-btn {
          display: flex; align-items: center; gap: 6px;
          padding: 8px 16px; border-radius: 20px;
          border: none; background: rgba(30,41,59,0.5);
          color: rgba(148,163,184,0.8); font-size: 13px; font-weight: 500;
          cursor: pointer; font-family: inherit;
          transition: all 180ms;
        }
        .ps-action-btn:active { transform: scale(0.94); }
        .ps-action-btn.liked {
          background: rgba(239,68,68,0.12); color: #f87171;
        }
        .ps-action-btn.active {
          background: rgba(139,92,246,0.12); color: #a78bfa;
        }
        .ps-action-icon { font-size: 16px; }

        /* Comments */
        .ps-comments {
          padding: 0 16px 14px;
          border-top: 1px solid rgba(255,255,255,0.04);
        }
        .ps-comments-loading, .ps-no-comments {
          padding: 16px 0; text-align: center;
          font-size: 13px; color: rgba(148,163,184,0.5);
        }
        .ps-comment {
          display: flex; gap: 10px; padding: 10px 0;
          border-bottom: 1px solid rgba(255,255,255,0.03);
        }
        .ps-comment:last-of-type { border-bottom: none; }
        .ps-comment-avatar {
          width: 28px; height: 28px; border-radius: 50%;
          background: linear-gradient(135deg, #6366f1, #8b5cf6);
          display: flex; align-items: center; justify-content: center;
          color: white; font-size: 11px; font-weight: 700; flex-shrink: 0;
        }
        .ps-comment-body { flex: 1; min-width: 0; }
        .ps-comment-header {
          display: flex; align-items: center; gap: 8px; margin-bottom: 4px;
        }
        .ps-comment-name { font-size: 13px; font-weight: 600; color: #c4b5fd; }
        .ps-comment-time { font-size: 11px; color: rgba(148,163,184,0.4); }
        .ps-comment-del {
          font-size: 11px; color: rgba(239,68,68,0.6); background: none;
          border: none; cursor: pointer; padding: 0 4px; font-family: inherit;
        }
        .ps-comment-del:hover { color: #f87171; }
        .ps-comment-text {
          font-size: 14px; line-height: 1.5; color: rgba(226,232,240,0.85);
          word-break: break-word;
        }
        .ps-comment-input {
          display: flex; gap: 8px; margin-top: 10px;
        }
        .ps-comment-field {
          flex: 1; padding: 10px 14px; border-radius: 20px;
          border: 1px solid rgba(148,163,184,0.2);
          background: rgba(30,41,59,0.6);
          color: #e2e8f0; font-size: 13px; font-family: inherit;
          outline: none;
        }
        .ps-comment-field:focus {
          border-color: rgba(139,92,246,0.5);
          box-shadow: 0 0 0 2px rgba(139,92,246,0.15);
        }
        .ps-comment-field::placeholder { color: rgba(148,163,184,0.4); }
        .ps-comment-send {
          padding: 8px 16px; border-radius: 20px; border: none;
          background: linear-gradient(90deg, #7c3aed, #6366f1);
          color: white; font-size: 13px; font-weight: 600;
          cursor: pointer; font-family: inherit;
          transition: all 150ms;
        }
        .ps-comment-send:disabled { opacity: 0.4; cursor: not-allowed; }
        .ps-comment-send:active { transform: scale(0.95); }

        /* Image Preview */
        .ps-preview-overlay {
          position: fixed; inset: 0; z-index: 9999;
          background: rgba(0,0,0,0.88);
          display: flex; align-items: center; justify-content: center;
          padding: 20px; animation: fadeIn 200ms;
        }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        .ps-preview-img {
          max-width: 100%; max-height: 100%; object-fit: contain;
        }
        @media (max-width:520px) {
          .ps-social-bar { align-items:stretch; flex-direction:column; padding:10px 12px 0; }
          .ps-feed-tabs { display:grid; grid-template-columns:1fr 1fr; }
          .ps-social-actions { display:grid; grid-template-columns:1fr 40px 1.25fr; }
          .ps-social-actions button { width:100%; }
          .ps-content { padding:14px 12px; }
          .ps-root { padding-bottom:0; }
        }
      `}</style>
    </div>
  );
};

export default PanSpiritWeb;
